import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const LANGS = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];

export const Navbar = () => {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathLang = location.pathname.split("/")[1];
  const restPath = location.pathname.split("/").slice(2).join("/");
  const hasHero = restPath === "" || restPath === "bellagio";
  const solid = scrolled || open || !hasHero;

  const changeLang = (code) => {
    setLang(code);
    const rest = location.pathname.split("/").slice(2).join("/");
    navigate(`/${code}${rest ? "/" + rest : ""}${location.search}${location.hash}`);
    setOpen(false);
  };

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: `/${lang}`, label: t("nav.home"), testId: "nav-home", end: true },
    { to: `/${lang}/bellagio`, label: t("nav.bellagio"), testId: "nav-bellagio" },
    { to: `/${lang}/camere`, label: t("nav.rooms"), testId: "nav-rooms" },
    { to: `/${lang}/chi-siamo`, label: t("nav.about"), testId: "nav-about" },
    { to: `/${lang}/come-arrivare`, label: t("nav.location"), testId: "nav-location" },
    { to: `/${lang}/servizi-trasporti`, label: t("nav.services"), testId: "nav-services" },
    { to: `/${lang}/recensioni`, label: t("nav.reviews"), testId: "nav-reviews" },
    { to: `/${lang}/blog`, label: t("nav.blog"), testId: "nav-blog" },
    { to: `/${lang}/faq`, label: "FAQ", testId: "nav-faq" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 h-20 flex items-center justify-between">
        <Link to={`/${lang}`} data-testid="nav-logo" className="group flex items-center gap-3">
          <img src="/assets/682gladr_logo.png"
               alt="I Lake It Bellagio logo"
               className="h-11 w-11 object-contain transition-all"
               style={solid ? {} : { filter: "brightness(0) invert(1)" }} />
          <span className={`font-brand text-base md:text-lg tracking-[0.14em] uppercase font-extrabold ${solid ? "text-[#1D3557]" : "text-white"}`}>
            I LAKE IT <span className="font-medium tracking-[0.18em]">BELLAGIO</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-sm font-medium tracking-wide transition-colors ${
                  solid ? "text-[#1A1A1A]/80 hover:text-[#345B73]" : "text-white/90 hover:text-white"
                } ${isActive ? "!text-[#345B73]" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition-all ${
                  solid ? "text-[#1A1A1A] hover:bg-black/5" : "text-white hover:bg-white/10"
                }`}
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium">{lang.toUpperCase()}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[6rem]">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => changeLang(l.code)}
                  className={lang === l.code ? "font-semibold text-[#345B73]" : ""}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to={`/${lang}/prenota`}
            data-testid="nav-book-now"
            className="hidden md:inline-flex items-center px-5 py-2.5 rounded-full bg-[#1D3557] text-white text-sm font-medium hover:bg-[#345B73] transition-all"
          >
            {t("nav.bookNow")}
          </Link>

          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 rounded-md ${solid ? "text-[#1A1A1A]" : "text-white"}`}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`mobile-${l.testId}`}
                className={({ isActive }) =>
                  `text-base font-medium py-2 ${isActive ? "text-[#345B73]" : "text-[#1A1A1A]/80"}`
                }>
                {l.label}
              </NavLink>
            ))}
            <Link to={`/${lang}/prenota`} data-testid="mobile-book-now"
              className="mt-3 text-center px-5 py-3 rounded-full bg-[#1D3557] text-white font-medium">
              {t("nav.bookNow")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
